import type { CodeLabel, RfqSortField, RfqStatusCode } from './rfq.types';

/**
 * The feature the RBAC matrix scores the RFQ screens against. Every guard, button and route names it,
 * and the backend's `@perm.on('Request For Quotation', …)` answers to the same row.
 */
export const RFQ_FEATURE = 'Request For Quotation';

/**
 * The nine statuses, in the order the reference's status filter lists them after "All Status".
 *
 * This is the contract's enum, not sample data: the filter asks `GET /rfqs/statuses` first and falls back
 * to this list only when that read fails, so the filter never renders empty.
 */
export const RFQ_STATUSES: readonly CodeLabel[] = [
  { code: 'UNDER_EVALUATION', label: 'Under Evaluation' },
  { code: 'DRAFT', label: 'Draft' },
  { code: 'PUBLISHED', label: 'Published' },
  { code: 'APPROVAL_PENDING', label: 'Approval Pending' },
  { code: 'FORCE_CLOSED', label: 'Force Closed' },
  { code: 'APPROVED', label: 'Approved' },
  { code: 'EXPIRED', label: 'Expired' },
  { code: 'ON_HOLD', label: 'On Hold' },
  { code: 'REFER_BACK', label: 'Refer Back' },
  { code: 'AWARD_APPROVAL_PENDING', label: 'Award Approval Pending' },
];

/** The reference's pill tint per status, as a BEM modifier (see `.rfq-pill--*` in rfqs.css). */
export const STATUS_TONE: Readonly<Record<RfqStatusCode, string>> = {
  DRAFT: 'grey',
  PUBLISHED: 'blue',
  UNDER_EVALUATION: 'violet',
  APPROVAL_PENDING: 'amber',
  FORCE_CLOSED: 'red',
  APPROVED: 'green',
  EXPIRED: 'grey',
  ON_HOLD: 'hold',
  REFER_BACK: 'orange',
  AWARD_APPROVAL_PENDING: 'amber',
};

/** A record the platform will not move again, so no lifecycle action is offered on it. */
export const TERMINAL_STATUSES: readonly string[] = ['APPROVED', 'EXPIRED', 'FORCE_CLOSED'];

/*
  The lifecycle actions, offered exactly where the platform accepts them (`RfqLifecycleService`,
  `RfqSupplierService`): Hold on any non-terminal record that is not already On Hold (a Draft or Refer
  Back is still being written, so it is not offered there); Resume on On Hold; Extend Deadline on
  Published; a supplier's withdrawal on Published or Under Evaluation.
*/
export const canHold = (status: string): boolean =>
  !TERMINAL_STATUSES.includes(status) && !['ON_HOLD', 'DRAFT', 'REFER_BACK'].includes(status);
export const canResume = (status: string): boolean => status === 'ON_HOLD';
export const canExtendDeadline = (status: string): boolean => status === 'PUBLISHED';
export const canWithdrawSupplier = (status: string): boolean => status === 'PUBLISHED' || status === 'UNDER_EVALUATION';
/*
  Force Close ends bidding early (ticket, 2026-11-02): only a Published BID, which then moves to Under Evaluation with
  its submitted quotations frozen and unsubmitted drafts excluded.
*/
export const canForceClose = (status: string): boolean => status === 'PUBLISHED';
export const FORCE_CLOSE_PUBLISHED_ONLY = 'Only a Published BID can be force closed';

/*
  The statuses whose BID Overview carries "More Actions ▾" (`AI_Control.html`, 2026-11-02): BID Extension, On Hold
  and Force Close. A Draft is deleted rather than closed, and an On Hold BID offers Resume BID on its own.
*/
export const MORE_ACTIONS_STATUSES: readonly string[] = ['PUBLISHED', 'UNDER_EVALUATION', 'APPROVAL_PENDING', 'REFER_BACK'];

/** A BID's deadline may be extended at most this many times (2026-10-27); the platform refuses another. */
export const MAX_DEADLINE_EXTENSIONS = 3;
export const EXTENSION_LIMIT_TIP = `The deadline can be extended at most ${String(MAX_DEADLINE_EXTENSIONS)} times`;
export const EXTENSION_PUBLISHED_ONLY = 'Only Published BIDs can be extended';
/** Whether the extension limit is spent: Extend Deadline is then shown disabled. */
export const extensionLimitReached = (deadlineExtensions: number | null | undefined): boolean =>
  (deadlineExtensions ?? 0) >= MAX_DEADLINE_EXTENSIONS;

export const EDIT_REFUSED_TIP = 'Only Draft and Refer Back records can be edited';

export const PAGE_SIZE_OPTIONS: readonly number[] = [5, 10, 25, 50];
export const LIST_PAGE_SIZE = 25;
export const SEARCH_DEBOUNCE_MS = 300;

/** The list's sortable columns, in the table's order. */
export const LIST_COLUMNS: readonly { key: RfqSortField | 'bids' | 'action'; label: string; sortable: boolean }[] = [
  { key: 'rfqNumber', label: 'BID NUMBER', sortable: true },
  { key: 'title', label: 'BID TITLE', sortable: true },
  { key: 'category', label: 'CATEGORY', sortable: true },
  { key: 'initiator', label: 'INITIATOR', sortable: true },
  { key: 'bidStart', label: 'BID START', sortable: true },
  { key: 'bidDeadline', label: 'BID DEADLINE', sortable: true },
  { key: 'bids', label: 'BIDS', sortable: false },
  { key: 'status', label: 'STATUS', sortable: true },
  { key: 'action', label: 'ACTION', sortable: false },
];

/** The column keys the export's `columns=` names, in the table's order. */
export const EXPORT_COLUMNS: readonly string[] = [
  'rfqNumber',
  'title',
  'category',
  'initiator',
  'bidStart',
  'bidDeadline',
  'bids',
  'status',
];

/** The wizard steps' own names, used when the template names a step none (the template's titles come first). */
export const WIZARD_STEPS: readonly string[] = [
  'BID Details',
  'Add Product & Purchase Request',
  'Select Supplier',
  'Delivery Instructions',
  'Review & Submit',
];

/** The submit endpoint keys each failure `<step>.<field>`; these are the steps, by wizard position. */
export const SUBMIT_STEP_KEYS: Readonly<Record<string, number>> = {
  details: 1,
  products: 2,
  suppliers: 3,
  delivery: 4,
  /* The acknowledgement sits on BID Details. */
  declaration: 1,
};

/**
 * The platform's default declaration until templates store one (G43). Shown only when the draft carries
 * no text of its own -- the platform is the source, this is the same sentence it defaults to.
 */
export const DEFAULT_DECLARATION =
  'I confirm that the details provided in this BID are accurate, that the selected suppliers meet eligibility criteria, and that this request complies with organizational procurement policy. I understand that submitting this BID initiates the supplier bidding process.';

export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * The z-index of an open dropdown list: the top of the stack (2026-10-26). PrimeReact raises a dialog by its modal base
 * (~1000) every time another kind of layer has opened, so deep in the wizard the Add location popup climbs past 9999 --
 * a list capped there opened beneath it and looked as if it never opened. The topmost tooltip uses the same value
 * (`.app-tooltip--topmost` in `index.css`).
 */
export const TOPMOST_POPUP_Z_INDEX = 2147483000;

/** RFQ-level supporting documents: "PDF, DOCX, XLSX, JPG, PNG up to 10 MB each" (PNG added 2026-10-27). */
export const RFQ_DOC_EXTENSIONS: readonly string[] = ['pdf', 'docx', 'xlsx', 'jpg', 'jpeg', 'png'];

/** A hold's or a resume's supporting document: "PDF, DOC, XLS, JPG or PNG · up to 10 MB" (2026-11-02). */
export const LIFECYCLE_DOC_EXTENSIONS: readonly string[] = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png'];

/** At most this many BID-level supporting documents (BID Details' drop zone; line documents do not count). */
export const MAX_RFQ_DOCUMENTS = 3;

/** At most this many documents per product line ("Max 3 files · up to 10 MB each", 2026-10-22). */
export const MAX_LINE_DOCUMENTS = 3;

/** Line documents: PDF, DOC, DOCX, XLS, XLSX, CSV, JPG, JPEG, PNG up to 10 MB each. */
export const LINE_DOC_EXTENSIONS: readonly string[] = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'jpg',
  'jpeg',
  'png',
];

